import { clearSubjectQuestionContext, isTopicModule, shouldOpenSubjectTopics } from './questionBank/qbankNavigation';
import { EXPLORE_ROUTES } from "./homeDashboard/exploreRoutes";
import { LECTURE_ROUTES } from "./homeDashboard/lecture/lectureRoutes";
import { GAMIFICATION_ROUTES } from "./homeDashboard/gamification/gamificationRoutes";
import { QUESTION_BANK_ROUTES } from "./questionBank/questionBankRoutes";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./QuestionBankSubjects.css";
import "./homeDashboard/gamification/GamificationSubjectCategories.css";
import fallbackSubjectImage from "./assets/images/battlesubject.jpg";
import coinImage from "./assets/images/coiin.png";
import { useQuestionBankSubjectDetail } from "./hooks/useQuestionBankSubjectDetail";
import GameOptionPopup from "./homeDashboard/gamification/SelectRoomTypeModal";
import { buildQuestionBankBackTarget, buildQuestionBankFlowState } from "./questionBank/qbankFlowState";
import { buildLectureFlowState, persistLectureFlowState } from "./homeDashboard/lecture/lectureFlowState";
import { RAPID_FIRE_ROUTES } from "./homeDashboard/rapidFire/rapidFireRoutes";
import { FLASH_CARD_ROUTES } from "./homeDashboard/flashcard/flashCardRoutes";
import { CONTENT_ROUTES } from "./homeDashboard/contentRoutes";
import {
  buildGamificationBackTarget,
  buildGamificationFlowState,
  getStoredGamificationFlowState,
  persistGamificationFlowState,
} from "./homeDashboard/gamification/gamificationFlowState";

const NOTES_CATEGORY_ID = 4;
const LECTURES_CATEGORY_ID = 3;
const FLASH_CARDS_CATEGORY_ID = 9;
const PODCAST_CATEGORY_ID = 12;
const INFOGRAPHICS_CATEGORY_ID = 13;
function SubjectCardShimmer() {
  return (
    <article className="subject-info-card subject-info-card--shimmer" aria-hidden="true">
      <div className="subject-info-card__main">
        <div className="subject-info-card__image">
          <span className="dashboard-skeleton dashboard-skeleton--thumb subject-info-card__shimmer-image" />
        </div>
        <div className="subject-info-card__content">
          <div className="subject-info-card__descriptionWrap">
            <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title subject-info-card__shimmer-title" />
            <span className="dashboard-skeleton dashboard-skeleton--line subject-info-card__shimmer-line" />
            <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short subject-info-card__shimmer-line subject-info-card__shimmer-line--short" />
          </div>
        </div>
      </div>
      <span className="dashboard-skeleton dashboard-skeleton--button subject-info-card__shimmer-button" />
    </article>
  );
}

function buildVisiblePages(currentPage, totalPages) {
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const halfWindow = Math.floor(maxVisiblePages / 2);
  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = endPage - maxVisiblePages + 1;
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
}

function resetGamificationLiveState(flowState) {
  return {
    ...flowState,
    battle_room_id: 0,
    temp_room_id: 0,
    room_code: "",
    player_id: 0,
    users_list: [],
    queue_users: [],
    currentQuestion: null,
    answerSaveResponse: null,
    result_snapshot: null,
    question_id: 0,
    question_index: 0,
    total_questions: 0,
    remaining_coins: 0,
    start_confirmation_required: false,
    start_countdown_seconds: 0,
    start_confirmed: false,
    liveStage: "",
    battleLeaderboardTarget: null,
    socketError: "",
  };
}

const SubjectInfoCard = ({ subject, requestParams, navigationState, topicsEnabled = false, isRapidFireCategory = false, isGamificationCategory = false, isExamCourseFlow = false }) => {
  const navigate = useNavigate();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);
  const [isGameOptionOpen, setIsGameOptionOpen] = useState(false);
  const {
    data: subjectDetail,
    isLoading: isSubjectDetailLoading,
    isError: hasSubjectDetailError,
    error: subjectDetailError,
  } = useQuestionBankSubjectDetail(
    {
      ...requestParams,
      subject_id: subject.id,
    },
    {
      enabled: !isGamificationCategory && isInfoExpanded && Boolean(subject?.id),
    }
  );

  const subjectFlowState = buildQuestionBankFlowState({
    ...navigationState,
    ...(!isExamCourseFlow && isTopicModule(requestParams?.category_id || 2) ? clearSubjectQuestionContext() : {}),
    selectedSubject: subject,
    subject_id: Number(subject.id),
    state_id: Number(requestParams?.states_id || 0),
    category_id: Number(requestParams?.category_id || 2),
  });
  const subjectsPath = navigationState?.subjectsPath || EXPLORE_ROUTES.subjects;

  const toggleDescription = () => {
    setIsDescriptionExpanded((current) => !current);
  };

  const handleMoreInfo = () => {
    setIsInfoExpanded((current) => !current);
  };

  const handleSubjectOpen = () => {
    const categoryId = Number(requestParams?.category_id || 2);
    const isFlashCardsCategory = categoryId === FLASH_CARDS_CATEGORY_ID;
    const isPodcastCategory = categoryId === PODCAST_CATEGORY_ID;
    navigate(EXPLORE_ROUTES.topics, {
      state: buildQuestionBankFlowState({
        ...subjectFlowState,
        topicRequestParams: {
          state_id: Number(requestParams?.states_id || 0),
          subject_id: Number(subject.id),
          category_id: categoryId,
          ...(Number(subjectFlowState.exam_id) > 0 ? { exam_id: Number(subjectFlowState.exam_id) } : {}),
          ...(isFlashCardsCategory
            ? {
                exam_id: Number(subjectFlowState.exam_id || 0),
                free_paid: requestParams?.free_paid || "",
              }
            : {}),
          ...(isPodcastCategory && requestParams?.languages
            ? { languages: requestParams.languages }
            : {}),
        },
        exams_id: !isExamCourseFlow && isTopicModule(requestParams?.category_id || 2) ? 0 : Number(requestParams?.exams_id || navigationState?.exams_id || 0),
        exam_id: !isExamCourseFlow && isTopicModule(categoryId) ? 0 : Number(requestParams?.exams_id || navigationState?.exam_id || 0),
        free_paid: !isExamCourseFlow && isTopicModule(requestParams?.category_id || 2) ? "" : requestParams?.free_paid || navigationState?.free_paid || "",
        freePaidResources: navigationState?.freePaidResources,
        qbankBackTarget: buildQuestionBankBackTarget(subjectsPath, navigationState || subjectFlowState),
      }),
    });
  };

  const handleRapidFireOpen = () => {
    navigate(RAPID_FIRE_ROUTES.start, {
      state: {
        ...subjectFlowState,
        selectedSubject: subject,
        subject_id: Number(subject.id),
        state_id: Number(requestParams?.states_id || 0),
        selectedState: navigationState?.selectedState || {
          state_id: Number(requestParams?.states_id || 0),
        },
        category_id: Number(requestParams?.category_id || 5),
      },
    });
  };

  const handleQuestionListOpen = () => {
    const listFlowState = buildQuestionBankFlowState({
        ...subjectFlowState,
        selectedTopic: null,
        selectedSubtopic: null,
        topic_id: 0,
        is_topic_specific: 0,
        is_parent_topic: 0,
        exams_id: !isExamCourseFlow && isTopicModule(requestParams?.category_id || 2) ? 0 : Number(requestParams?.exams_id || navigationState?.exams_id || 0),
        exam_id: !isExamCourseFlow && isTopicModule(requestParams?.category_id || 2) ? 0 : Number(requestParams?.exams_id || navigationState?.exam_id || 0),
        free_paid: !isExamCourseFlow && isTopicModule(requestParams?.category_id || 2) ? "" : requestParams?.free_paid || navigationState?.free_paid || "",
        subTopicRequestParams: null,
        qbankBackTarget: buildQuestionBankBackTarget(subjectsPath, navigationState || subjectFlowState),
      });
    const nextState = isLecturesCategory ? buildLectureFlowState(listFlowState) : listFlowState;
    if (isLecturesCategory) persistLectureFlowState(nextState);
    navigate(isNotesCategory ? CONTENT_ROUTES.notes : isLecturesCategory ? LECTURE_ROUTES.list : isPodcastCategory ? CONTENT_ROUTES.podcasts : isFlashCardsCategory ? FLASH_CARD_ROUTES.start : isInfographicsCategory ? CONTENT_ROUTES.infographics : QUESTION_BANK_ROUTES.list, { state: nextState });
  };

  const handleGamificationOpen = () => {
    const battleFlowState = resetGamificationLiveState(
      buildGamificationFlowState(
        {
          ...subjectFlowState,
          entryMode: "random",
          selectedSubject: subject,
          selectedBattle: navigationState?.selectedBattle || subjectFlowState?.selectedBattle || null,
          subject_id: Number(subject.id),
          state_id: Number(requestParams?.states_id || 0),
          selectedState: navigationState?.selectedState || {
            state_id: Number(requestParams?.states_id || 0),
          },
          category_id: Number(requestParams?.category_id || 6),
          gamificationBackTarget: buildGamificationBackTarget(EXPLORE_ROUTES.subjects, navigationState || subjectFlowState),
        },
        getStoredGamificationFlowState()
      )
    );
    const selectedBattleId = Number(
      battleFlowState?.selectedBattle?.battles_id ||
        battleFlowState?.selectedBattle?.battle_id ||
        battleFlowState?.selectedBattle?.id ||
        0
    );
    const nextPath =
      selectedBattleId === 1
        ? GAMIFICATION_ROUTES.groupWaiting
        : selectedBattleId === 2
          ? GAMIFICATION_ROUTES.singleWaiting
          : GAMIFICATION_ROUTES.start;

    persistGamificationFlowState(battleFlowState);
    navigate(nextPath, { state: battleFlowState });
  };
  const handlePlayWithFriend = () => {
    const friendFlowState = resetGamificationLiveState(
      buildGamificationFlowState({
        ...subjectFlowState,
        entryMode: "friend",
        selectedSubject: subject,
        selectedBattle: navigationState?.selectedBattle || subjectFlowState?.selectedBattle || null,
        subject_id: Number(subject.id),
        state_id: Number(requestParams?.states_id || 0),
        selectedState: navigationState?.selectedState || {
          state_id: Number(requestParams?.states_id || 0),
        },
        category_id: Number(requestParams?.category_id || 6),
        gamificationBackTarget: buildGamificationBackTarget(EXPLORE_ROUTES.subjects, navigationState || subjectFlowState),
      })
    );

    persistGamificationFlowState(friendFlowState);
    navigate(GAMIFICATION_ROUTES.playFriends, { state: friendFlowState });
  };

  const isNotesCategory = Number(requestParams?.category_id || 0) === NOTES_CATEGORY_ID;
  const isLecturesCategory = Number(requestParams?.category_id || 0) === LECTURES_CATEGORY_ID;
  const isFlashCardsCategory = Number(requestParams?.category_id || 0) === FLASH_CARDS_CATEGORY_ID;
  const isPodcastCategory = Number(requestParams?.category_id || 0) === PODCAST_CATEGORY_ID;
  const isInfographicsCategory = Number(requestParams?.category_id || 0) === INFOGRAPHICS_CATEGORY_ID;
  const progressValue = subjectDetail?.correct_guess_percentage || "0.00%";
  const progressNumericValue = Number.parseFloat(String(progressValue).replace("%", "")) || 0;
  const rapidfireAttemptedCount = Number(
    subjectDetail?.rapidfire_attempted_count ?? subject.rapidfireAttemptedCount ?? 0
  );
  const rapidfireQuestionCount = Number(
    subjectDetail?.total_attempted_question ?? subject.totalAttemptedQuestion ?? 0
  );
  const rapidfireEntryFee = Number(subjectDetail?.game_entry_fee ?? subject.gameEntryFee ?? 0);
  const gamificationCoins = Number(subject.gameEntryFee ?? 0);
  const canUseExamTopicFlow =
    isExamCourseFlow &&
    String(requestParams?.free_paid || '').trim().toUpperCase() !== 'F';
  const hasForcedExamTopicFlow =
    subject.forceTopicsEnabled && subject.forceSubtopicsEnabled;
  const hasToggleExamTopicFlow =
    topicsEnabled &&
    subject.subjectTopicVisibilityStatus !== 0 &&
    subject.topicsCount > 0;
  const shouldOpenExamTopics =
    canUseExamTopicFlow &&
    (hasForcedExamTopicFlow || hasToggleExamTopicFlow);
  const shouldOpenTopics =
    !isExamCourseFlow
      ? isTopicModule(requestParams?.category_id || 2) ? shouldOpenSubjectTopics(subject, topicsEnabled) : topicsEnabled
      : shouldOpenExamTopics;
  const handleCardClick = () => {
    if (shouldOpenTopics && !isRapidFireCategory && !isGamificationCategory) {
      handleSubjectOpen();
      return;
    }

    if (isRapidFireCategory) {
      handleRapidFireOpen();
      return;
    }

    if (isGamificationCategory) {
      return;
    }

    handleQuestionListOpen();
  };

  return (
    <>
      <article
        className={`subject-info-card qbankStyle ${shouldOpenTopics && !isRapidFireCategory && !isGamificationCategory ? " subject-info-card--clickable" : ""}`}
        onClick={handleCardClick}
        onKeyDown={(event) => {
          if ((event.key === "Enter" || event.key === " ") && !isGamificationCategory) {
            event.preventDefault();
            handleCardClick();
          }
        }}
        role={isGamificationCategory ? undefined : "button"}
        tabIndex={isGamificationCategory ? undefined : 0}
      >
        <div className="subject-info-card__main">
          <div className="subject-info-card__image">
            <img
              src={subject.image}
              alt={`${subject.title} subject`}
            />
          </div>

          <div className="subject-info-card__content">
            <h3 className="subject-info-card__title">
              {subject.title}
            </h3>

            <div className="subject-info-card__descriptionWrap">
              <p
                className={`subject-info-card__description ${
                  isDescriptionExpanded ? "is-expanded" : ""
                }`}
              >
                {subject.description}
              </p>

              <button
                type="button"
                className="subject-info-card__descriptionToggle"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleDescription();
                }}
                aria-label={
                  isDescriptionExpanded
                    ? `Show less description for ${subject.title}`
                    : `Show more description for ${subject.title}`
                }
              >
                {isDescriptionExpanded ? "Show less" : "Show more"}
              </button>
            </div>
          </div>
        </div>

        {!isInfoExpanded && isGamificationCategory ? (
          <div className="game-card-content">
            <div className="game-reward">
              <img className="imgCoin" src={coinImage} alt="" />
              <span>{`${gamificationCoins} Coins`}</span>
            </div>

            <button
              className="game-start-btn"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setIsGameOptionOpen(true);
              }}
              aria-label={`Start ${subject.title}`}
            >
              <span>Start</span>
              <i className="bi bi-play-circle-fill" aria-hidden="true"></i>
            </button>


          </div>
        ) : !isInfoExpanded ? (
          <button
            type="button"
            className="subject-info-card__action"
            onClick={(event) => {
              event.stopPropagation();
              handleMoreInfo();
            }}
            aria-label={`Get more information about ${subject.title}`}
          >
            <span className="subject-info-card__action-text">
              <i className="bi bi-info-circle" aria-hidden="true"></i>
              Get More Info
            </span>

            <i
              className="fa-solid fa-chevron-right subject-info-card__arrow"
              aria-hidden="true"
            />
          </button>
        ) : null}

        {isInfoExpanded && (
          <div className="subject-info-card__details" aria-label={`${subject.title} progress details`}>
            {isSubjectDetailLoading ? (
              <div className="subject-info-card__detailsShimmer" aria-hidden="true">
                <span className="dashboard-skeleton dashboard-skeleton--line subject-info-card__detailsShimmer-bar" />
                <div className="subject-info-card__stats">
                  <span className="dashboard-skeleton dashboard-skeleton--line subject-info-card__detailsShimmer-stat" />
                  <span className="dashboard-skeleton dashboard-skeleton--line subject-info-card__detailsShimmer-stat" />
                  <span className="dashboard-skeleton dashboard-skeleton--line subject-info-card__detailsShimmer-stat" />
                </div>
              </div>
            ) : null}
            {!isSubjectDetailLoading && hasSubjectDetailError ? (
              <div className="cs-status">
                {subjectDetailError?.response?.data?.message || subjectDetailError?.message || "Unable to load subject details."}
              </div>
            ) : null}
            {!isSubjectDetailLoading && !hasSubjectDetailError && isRapidFireCategory ? (
              <div className="subject-info-card__stats">
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Attempts</span>
                  <span className="subject-info-card__stat-value">{rapidfireAttemptedCount}</span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Questions</span>
                  <span className="subject-info-card__stat-value">{rapidfireQuestionCount}</span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Entry Fee</span>
                  <span className="subject-info-card__stat-value">{rapidfireEntryFee}</span>
                </div>
              </div>
            ) : null}
            {!isSubjectDetailLoading && !hasSubjectDetailError && !isRapidFireCategory && isNotesCategory ? (
              <div className="subject-info-card__stats">
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Topics</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.topics_count ?? subject.topicsCount}
                  </span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Notes</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.total_notes ?? 0}
                  </span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Time</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.total_notes_time || "00:00:00"}
                  </span>
                </div>
              </div>
            ) : null}
            {!isSubjectDetailLoading && !hasSubjectDetailError && !isRapidFireCategory && isFlashCardsCategory ? (
              <div className="subject-info-card__stats">
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Topics</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.topics_count ?? subject.topicsCount}
                  </span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Flash Cards</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.total_flash_cards ?? 0}
                  </span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Time</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.total_flash_card_time || "00:00:00"}
                  </span>
                </div>
              </div>
            ) : null}
            {!isSubjectDetailLoading && !hasSubjectDetailError && !isRapidFireCategory && isPodcastCategory ? (
              <div className="subject-info-card__stats">
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Topics</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.topics_count ?? subject.topicsCount}
                  </span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Podcasts</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.total_audio_podcasts ?? 0}
                  </span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Time</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.total_audio_podcast_time || "00:00:00"}
                  </span>
                </div>
              </div>
            ) : null}
            {!isSubjectDetailLoading && !hasSubjectDetailError && !isRapidFireCategory && isInfographicsCategory ? (
              <div className="subject-info-card__stats">
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Topics</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.topics_count ?? subject.topicsCount}
                  </span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Infographics</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.total_infographics ?? 0}
                  </span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Time</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.total_infographics_time || "00:00:00"}
                  </span>
                </div>
              </div>
            ) : null}
            {!isSubjectDetailLoading && !hasSubjectDetailError && !isRapidFireCategory && isLecturesCategory ? (
              <div className="subject-info-card__stats">
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Topics</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.topics_count ?? subject.topicsCount}
                  </span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Lectures</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.total_lectures ?? 0}
                  </span>
                </div>
                <div className="subject-info-card__stat">
                  <span className="subject-info-card__stat-label">Time</span>
                  <span className="subject-info-card__stat-value">
                    {subjectDetail?.total_lectures_time || "00:00:00"}
                  </span>
                </div>
              </div>
            ) : null}
            {!isSubjectDetailLoading && !hasSubjectDetailError && !isRapidFireCategory && !isNotesCategory && !isFlashCardsCategory && !isPodcastCategory && !isInfographicsCategory && !isLecturesCategory ? (
              <>
                <div className="subject-info-card__progressRow">
                  <div className="subject-info-card__progressTrack" aria-hidden="true">
                    <div
                      className="subject-info-card__progressFill"
                      style={{ width: `${Math.max(0, Math.min(100, progressNumericValue))}%` }}
                    />
                  </div>
                  <span className="subject-info-card__progressValue">
                    {progressValue}
                  </span>
                </div>

                <div className="subject-info-card__stats">
                  <div className="subject-info-card__stat">
                    <span className="subject-info-card__stat-label">Guess Score</span>
                    <span className="subject-info-card__stat-value">
                      {progressValue}
                    </span>
                  </div>
                  <div className="subject-info-card__stat">
                    <span className="subject-info-card__stat-label">Topics Cover</span>
                    <span className="subject-info-card__stat-value">
                      {subjectDetail?.topics_count ?? subject.topicsCount}
                    </span>
                  </div>
                  <div className="subject-info-card__stat">
                    <span className="subject-info-card__stat-label">Questions</span>
                    <span className="subject-info-card__stat-value">
                      {`${subjectDetail?.attempted_questions ?? 0}/${subjectDetail?.total_questions ?? subject.totalQuestions ?? 0}`}
                    </span>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}
      </article>

      {isGamificationCategory ? (
        <GameOptionPopup
          isOpen={isGameOptionOpen}
          onClose={() => setIsGameOptionOpen(false)}
          subjectName={subject.title}
          entryFee={gamificationCoins}
          onPlayFriend={() => {
            setIsGameOptionOpen(false);
            handlePlayWithFriend();
          }}
          onPlay={() => {
            setIsGameOptionOpen(false);
            handleGamificationOpen();
          }}
        />
      ) : null}
    </>
  );
};

const SubjectInfoCards = ({
  subjects = [],
  searchTerm = "",
  requestParams,
  pagination = {},
  currentPage = 1,
  onPageChange,
  isLoading = false,
  topicsEnabled = false,
  isRapidFireCategory = false,
  isGamificationCategory = false,
  isExamCourseFlow = false,
  navigationState,
}) => {
  const subjectData = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const normalizedData = subjects.map((item) => ({
      id: Number(item.subject_id),
      title: item.subject_name || "Subject",
      description: item.subject_description || "Description will be available soon.",
      image: item.subject_image || fallbackSubjectImage,
      topicsCount: Number(item.topics_count || 0),
      totalQuestions: Number(item.total_questions || 0),
      rapidfireAttemptedCount: Number(item.rapidfire_attempted_count || 0),
      totalAttemptedQuestion: Number(item.total_attempted_question || 0),
      gameEntryFee: Number(item.game_entry_fee || 0),
      forceTopicsEnabled: Number(item.force_topics_enabled || 0) === 1,
      forceSubtopicsEnabled: Number(item.force_subtopics_enabled || 0) === 1,
      subjectTopicVisibilityStatus: Number(item.subject_topic_visibility_status ?? 0),
    }));

    if (!normalizedSearch || (!isExamCourseFlow && isTopicModule(requestParams?.category_id || 2))) {
      return normalizedData;
    }

    return normalizedData.filter((item) => {
      const title = String(item.title || "").toLowerCase();
      const description = String(item.description || "").toLowerCase();

      return title.includes(normalizedSearch) || description.includes(normalizedSearch);
    });
  }, [searchTerm, subjects, isExamCourseFlow, requestParams?.category_id]);
  const totalPages = Number(pagination?.totalPages || 1);
  const visiblePages = buildVisiblePages(currentPage, totalPages);
  const isEmpty = !isLoading && !subjectData.length;

  return (
    <section
      className="subject-info-section"
      aria-label="Subject information"
    >
      {isLoading ? (
        <div className="subject-info-grid">
          {Array.from({ length: 6 }).map((_, index) => (
            <SubjectCardShimmer key={index} />
          ))}
        </div>
      ) : null}
      {!isLoading && subjectData.length ? (
        <div className="subject-info-grid">
          {subjectData.map((subject) => (
            <SubjectInfoCard
              key={subject.id}
              subject={subject}
              requestParams={requestParams}
              navigationState={navigationState}
              topicsEnabled={topicsEnabled}
              isRapidFireCategory={isRapidFireCategory}
              isGamificationCategory={isGamificationCategory}
              isExamCourseFlow={isExamCourseFlow}
            />
          ))}
        </div>
      ) : null}
      {isEmpty ? (
        <div className="cs-status">No subjects available.</div>
      ) : null}
      {!isLoading && totalPages > 1 ? (
        <div className="subject-info-pagination">
          <button
            type="button"
            className="subject-info-pagination__nav"
            disabled={currentPage <= 1}
            onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
          >
            Previous
          </button>
          <div className="subject-info-pagination__pages">
            {visiblePages.map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                className={`subject-info-pagination__page${pageNumber === currentPage ? " is-active" : ""}`}
                onClick={() => onPageChange?.(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="subject-info-pagination__nav"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange?.(Math.min(totalPages, currentPage + 1))}
          >
            Next
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default SubjectInfoCards;
